import { useTranslation } from 'react-i18next';
import { NotebookPen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { PatientNoteCard } from './PatientNoteCard';

/**
 * Header Note control + side sheet. Panel layout (not a nested Card) so the
 * title, scrollable history, and composer read cleanly at 1080p heights.
 */
type Props = {
  patientId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const PatientNotesSheet = ({ patientId, open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal>
      <SheetContent
        widthClass="w-full sm:w-[420px]"
        className="gap-0 overflow-hidden p-0"
        aria-describedby={undefined}
      >
        <SheetHeader className="shrink-0 space-y-1 border-b px-5 py-4 pr-12 text-left">
          <SheetTitle className="flex items-center gap-2 text-base">
            <NotebookPen className="h-4 w-4 text-muted-foreground" />
            {t('Clinician notes')}
          </SheetTitle>
          <SheetDescription>
            {t('Visible to the care team. Private to this patient.')}
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
          <PatientNoteCard patientId={patientId} variant="panel" />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const PatientNotesButton = ({
  onClick,
}: {
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-8 gap-1.5 px-2"
      onClick={onClick}
      title={t('Clinician notes')}
    >
      <NotebookPen className="h-3.5 w-3.5" />
      <span className="hidden text-xs font-medium sm:inline">{t('Note')}</span>
    </Button>
  );
};
